import React, { useState, useMemo } from 'react';
import { FileText, FolderPlus, Upload, Search, Filter, Grid, List } from 'lucide-react';
import { useDeals } from '../../store/DealsContext';
import DocumentList from './DocumentList';
import DocumentGrid from './DocumentGrid';
import DocumentUpload from './DocumentUpload';
import DocumentActions from './DocumentActions';
import DocumentFilters from './DocumentFilters';
import NewFolderModal from './NewFolderModal';
import { useFileUpload } from '../../hooks/useFileUpload';
import { sampleFiles, sampleFolders } from '../../data/dataRoomSample';
import { DataRoomFile, DataRoomFolder } from '../../types/dataRoom';

export default function DataRoom() {
  const { deals } = useDeals();
  const { uploadFiles, uploading, error } = useFileUpload();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocument, setSelectedDocument] = useState<DataRoomFile | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [showUpload, setShowUpload] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<string>('all');
  const [filterType, setFilterType] = useState('all');
  const [showActions, setShowActions] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<string>('/');

  // Filter and organize files
  const filteredFiles = useMemo(() => {
    return sampleFiles.filter(file => {
      const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' ? true : file.type === filterType;
      const matchesFolder = file.path.startsWith(currentFolder);
      return matchesSearch && matchesType && matchesFolder;
    });
  }, [searchTerm, filterType, currentFolder]);

  // Get current folder structure
  const currentFolderContents = useMemo(() => {
    const folders = sampleFolders.filter(folder => 
      folder.path.startsWith(currentFolder) && 
      folder.path !== currentFolder &&
      !folder.path.slice(currentFolder.length + 1).includes('/')
    );
    
    const files = filteredFiles.filter(file => 
      file.path === currentFolder
    );

    return { folders, files };
  }, [currentFolder, filteredFiles]);

  const handleUpload = async (files: File[]) => {
    await uploadFiles(files, selectedDeal);
    setShowUpload(false);
  };

  const breadcrumbs = currentFolder.split('/').filter(Boolean);

  return (
    <div className="h-full">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Virtual Data Room</h2>
          <p className="mt-1 text-gray-600">
            {filteredFiles.length} documents
          </p>
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={() => setShowUpload(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Files
          </button>
          <button 
            onClick={() => setShowNewFolder(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <FolderPlus className="w-4 h-4 mr-2" />
            New Folder
          </button>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="flex items-center space-x-2 mb-4">
        <button 
          onClick={() => setCurrentFolder('/')}
          className="text-sm text-gray-600 hover:text-blue-600"
        >
          Root
        </button>
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb}>
            <span className="text-gray-400">/</span>
            <button
              onClick={() => setCurrentFolder('/' + breadcrumbs.slice(0, index + 1).join('/'))}
              className="text-sm text-gray-600 hover:text-blue-600"
            >
              {crumb}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search files..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <DocumentFilters
              deals={deals}
              selectedDeal={selectedDeal}
              onDealChange={setSelectedDeal}
              onFilterChange={setFilterType}
            />

            <div className="flex items-center border border-gray-300 rounded-lg">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : ''}`}
              >
                <List className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : ''}`}
              >
                <Grid className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {viewMode === 'list' ? (
          <DocumentList
            folders={currentFolderContents.folders}
            documents={currentFolderContents.files}
            onFolderClick={(folder) => setCurrentFolder(folder.path)}
            onSelect={(doc) => {
              setSelectedDocument(doc);
              setShowActions(true);
            }}
          />
        ) : (
          <DocumentGrid
            folders={currentFolderContents.folders}
            documents={currentFolderContents.files}
            onFolderClick={(folder) => setCurrentFolder(folder.path)}
            onSelect={(doc) => {
              setSelectedDocument(doc);
              setShowActions(true);
            }}
          />
        )}
      </div>

      {showUpload && (
        <DocumentUpload
          onUpload={handleUpload}
          onClose={() => setShowUpload(false)}
          selectedDeal={selectedDeal}
          uploading={uploading}
          error={error}
        />
      )}

      {showNewFolder && (
        <NewFolderModal
          onClose={() => setShowNewFolder(false)}
          onSubmit={(name) => {
            // Handle new folder creation
            setShowNewFolder(false);
          }}
        />
      )}

      {showActions && selectedDocument && (
        <DocumentActions
          document={selectedDocument}
          onClose={() => {
            setShowActions(false);
            setSelectedDocument(null);
          }}
        />
      )}
    </div>
  );
}